/**
 * Logique métier pure — Verdict système + Actions prioritaires
 *
 * Règles (source : décision Doyen KT 2026-06-12) :
 *   - INSTABLE  si au moins 1 projet critical (non acquitté)
 *   - DÉGRADÉ   si au moins 1 projet warning  (non acquitté) et aucun critical
 *   - STABLE    sinon
 *
 * Toutes les fonctions sont pures, sans effet de bord.
 * Chaque verdict est traçable vers ses alertes sources.
 */

import type { Alert } from './api'

/* ── Types ─────────────────────────────────────────────────────── */

export type VerdictLevel = 'stable' | 'degraded' | 'unstable'

export interface SystemVerdict {
  level: VerdictLevel
  label: string
  description: string
  criticalCount: number
  warningCount: number
  /** Alertes à l'origine du verdict — cliquables dans l'UI */
  sourceAlerts: Alert[]
}

export interface TopAction {
  severity: 'critical' | 'high'
  message: string
  projectId: string
  /** Nombre de projets concernés par cette même alerte */
  affectedCount: number
  /** Alertes sources qui ont généré cette action */
  sourceAlerts: Alert[]
}

/* ── computeSystemVerdict ───────────────────────────────────────── */

export function computeSystemVerdict(
  buckets: { projectId: string; health: 'good' | 'warning' | 'critical' }[],
  alerts: Alert[]
): SystemVerdict {
  const unacked = alerts.filter((a) => !a.ackedAt)

  const criticalBuckets = buckets.filter((b) => b.health === 'critical')
  const warningBuckets  = buckets.filter((b) => b.health === 'warning')

  /* Les alertes qui correspondent aux projets critiques */
  const criticalProjectIds = new Set(criticalBuckets.map((b) => b.projectId))
  const warningProjectIds  = new Set(warningBuckets.map((b) => b.projectId))

  const criticalAlerts = unacked.filter(
    (a) => a.severity === 'critical' || criticalProjectIds.has(a.projectId)
  )
  const warningAlerts = unacked.filter(
    (a) => (a.severity === 'high' || warningProjectIds.has(a.projectId)) &&
           !criticalProjectIds.has(a.projectId)
  )

  if (criticalBuckets.length > 0 || criticalAlerts.length > 0) {
    return {
      level: 'unstable',
      label: 'INSTABLE',
      description: `${criticalBuckets.length} projet(s) critique(s) — action immédiate requise`,
      criticalCount: criticalBuckets.length,
      warningCount: warningBuckets.length,
      sourceAlerts: criticalAlerts,
    }
  }

  if (warningBuckets.length > 0 || warningAlerts.length > 0) {
    return {
      level: 'degraded',
      label: 'DÉGRADÉ',
      description: `${warningBuckets.length} projet(s) à surveiller`,
      criticalCount: 0,
      warningCount: warningBuckets.length,
      sourceAlerts: warningAlerts,
    }
  }

  return {
    level: 'stable',
    label: 'STABLE',
    description: 'Tous les projets sont opérationnels',
    criticalCount: 0,
    warningCount: 0,
    sourceAlerts: [],
  }
}

/* ── computeTopActions ──────────────────────────────────────────── */

/**
 * Retourne les actions prioritaires triées par sévérité puis par nombre
 * de projets impactés. Chaque action est traçable vers ses alertes sources.
 * Maximum `limit` actions (défaut 3 pour ne pas noyer l'utilisateur).
 */
export function computeTopActions(alerts: Alert[], limit = 3): TopAction[] {
  const unacked = alerts.filter((a) => !a.ackedAt)

  /* Ne garder que critical + high */
  const actionable = unacked.filter(
    (a) => a.severity === 'critical' || a.severity === 'high'
  )

  /* Regrouper par message normalisé (trim + lowercase) */
  const groups = new Map<string, Alert[]>()
  for (const alert of actionable) {
    const key = alert.message.trim().toLowerCase()
    const existing = groups.get(key) ?? []
    groups.set(key, [...existing, alert])
  }

  const actions: TopAction[] = Array.from(groups.entries())
    .filter(([, group]) => group.length > 0)
    .map(([, group]) => {
      /* Représentant = première alerte du groupe (la plus récente par insertion) */
      const rep = group[0] as Alert
      const affectedProjects = new Set(group.map((a) => a.projectId))
      return {
        severity: (rep.severity === 'critical' ? 'critical' : 'high') as TopAction['severity'],
        message: rep.message,
        projectId: rep.projectId,
        affectedCount: affectedProjects.size,
        sourceAlerts: group,
      }
    })

  /* Tri : critical avant high, puis par nombre de projets impactés (desc) */
  actions.sort((a, b) => {
    if (a.severity !== b.severity) {
      return a.severity === 'critical' ? -1 : 1
    }
    return b.affectedCount - a.affectedCount
  })

  return actions.slice(0, limit)
}
